import { AuditOutlined, SettingOutlined } from '@ant-design/icons';
import { createEnumOptions } from '@/utils/core/data-transformation';
import { getEnumNamespace } from '@/utils/infrastructure';
import { CoreEnums } from '@/utils/domain/constants';
import Filters, { registerModuleFilters } from '@/utils/input/filters';

const TypeFilter = {
  ...Filters.BaseFilters.EnumFilter,
  name: 'type',
  label: 'custom.lists.text.type',
  enumNamespace: getEnumNamespace(CoreEnums.CustomListType),
  enumSource: CoreEnums.CustomListType,
  options: createEnumOptions(CoreEnums.CustomListType, 'customList.select'),
};

const GroupFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'customListGroupId',
  label: 'global.text.group',
  // User-facing filter on `/custom-lists` → uses the access-aware
  // `list/groups` endpoint so non-admin roles only see the groups
  // they're allowed to filter by (matches the standard adopted by
  // `entity/groups`, `report/groups`, `process/groups`). The admin
  // CRUD endpoint `customListGroup/paged` is reserved for
  // `/configuration/custom-list-groups`.
  apiRoute: 'list/groups',
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const ProcessFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'processId',
  label: 'global.text.process',
  // `/custom-lists` is user-facing (no admin authority gate on the route).
  // Use the launch-permission endpoint so non-admin roles only see the
  // processes they can actually filter by, matching the base ProcessFilter
  // default (`@/utils/input/filters/ProcessFilter`).
  apiRoute: 'process/allowed',
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const IsPublicFilter = {
  ...Filters.BaseFilters.BooleanFilter,
  name: 'isPublic',
  label: 'global.text.isPublic',
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.createdDate',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const UpdatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.updatedDate',
  name: 'updatedDateRange',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

export const filtersConfig = [
  TypeFilter,
  GroupFilter,
  ProcessFilter,
  IsPublicFilter,
  Filters.IsActiveFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
];

registerModuleFilters('lists', [TypeFilter, GroupFilter, ProcessFilter, IsPublicFilter]);

export const getListOptionsItems = (formatMessage, { includeTaskTraces = false } = {}) => {
  const items = [
    {
      key: 'config',
      label: formatMessage({ id: 'global.text.config' }),
      icon: <SettingOutlined />,
    },
  ];
  if (includeTaskTraces) {
    items.push({
      key: 'taskTraces',
      label: formatMessage({ id: 'customList.listTaskTrace.title' }),
      icon: <AuditOutlined />,
    });
  }
  return items;
};

/** Per-item menu options — empty unless the backend granted `canManage`. */
export const getListOptionsItemsForItem = (item, formatMessage) => {
  if (item?.canManage !== true) return [];
  return getListOptionsItems(formatMessage, { includeTaskTraces: true });
};
